import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime, filter, Subject, switchMap, takeUntil } from 'rxjs';
import { SettingsOpsService } from '../../settings-ops/settings-ops.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-switch-user-modal',
    templateUrl: './switch-user-modal.component.html',
    styleUrls: ['./switch-user-modal.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SwitchUserModalComponent implements OnDestroy {
  title = 'Search for new user';
  selectedUser = '';
  loading = false;
  noRecords = false;
  showList = false;
  switchReset = true;
  userDetailsArr: string[] = [];

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private baseUrl: string;
  private adminApiUrl: string;

  constructor(
    public dialogRef: MatDialogRef<SwitchUserModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private settingsOpsService: SettingsOpsService,
    private snackbar: GtwSnackbarService,
  ) {
    this.baseUrl = data?.baseUrl || '';
    this.adminApiUrl = data?.adminApiUrl || '';

    this.searchSubject.pipe(
      debounceTime(500),
      filter((val: string) => val.trim().length >= 1),
      switchMap((val: string) => {
        this.loading = true;
        this.noRecords = false;
        this.userDetailsArr = [];
        return this.settingsOpsService.getUserDetails(this.baseUrl, val);
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (data: any) => {
        const users = data?.userDetails || [];
        this.userDetailsArr = users.map((u: any) => `${u.first_name} ${u.last_name} - ${u.sso_id}`);
        this.loading = false;
        this.showList = this.userDetailsArr.length > 0;
        this.noRecords = this.userDetailsArr.length === 0;
      },
      error: () => {
        this.loading = false;
        this.showList = false;
        this.noRecords = true;
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(): void {
    const search = (this.selectedUser || '').trim();
    if (!search) {
      this.userDetailsArr = [];
      this.noRecords = false;
      this.showList = false;
      return;
    }
    this.searchSubject.next(search);
  }

  fillTextbox(userData: string): void {
    this.selectedUser = userData;
    this.showList = false;
    this.switchReset = false;
  }

  reset(): void {
    this.selectedUser = '';
    this.userDetailsArr = [];
    this.switchReset = true;
    this.noRecords = false;
    this.showList = false;
  }

  switchUser(): void {
    const selected = this.selectedUser || '';
    const sso = selected.split('- ').pop() || '';
    if (!sso) {
      this.snackbar.openSnackBar('Please select a user', 'gtw-snackbar--yellow');
      return;
    }
    this.loading = true;
    this.settingsOpsService.changeSso(this.adminApiUrl, sso)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          if (String(resp?.callSuccess) === '1') {
            this.dialogRef.close('refresh');
          } else {
            this.snackbar.openSnackBar(resp?.message || 'Unable to switch user', 'gtw-snackbar--red');
          }
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Error while switching user', 'gtw-snackbar--red');
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
